import { BaseProvider } from "./base.provider";

export interface UserDailyCount {
  date: string;
  count: number;
}

export interface UsersHistoryResponse {
  total: number;
  history: UserDailyCount[];
}

export interface LoginHeatmapCell {
  h3_index: string;
  lat: number;
  lng: number;
  count: number;
}

export interface LoginTimeBucket {
  bucket: string; // "HH:MM", UTC, start of a 5-minute window
  count: number;
}

export interface LoginDailyCount {
  date: string; // "YYYY-MM-DD", UTC
  count: number;
}

// One websocket-connect "login" event, from the raw audit log.
export interface LoginEvent {
  ts: string;
  // "HH:MM" in the client's own timezone at the moment of login — omitted
  // when the timezone couldn't be resolved.
  local_time?: string;
  timezone?: string;
  ip?: string;
  user_agent?: string;
}

export interface LoginHistoryPage {
  data: LoginEvent[];
  page: number;
  page_size: number;
  has_next: boolean;
}

class AdminProvider extends BaseProvider {
  getUsersHistory(days: number = 365): Promise<UsersHistoryResponse> {
    return this.request<UsersHistoryResponse>(
      `/v1/admin/users/history?days=${days}`,
    );
  }

  getUsersHistoryAll(): Promise<UsersHistoryResponse> {
    return this.request<UsersHistoryResponse>(`/v1/admin/users/history`);
  }

  async getUsersCount(): Promise<number> {
    const { count } = await this.request<{ count: number }>(
      `/v1/admin/users/count`,
    );
    return count;
  }

  getUsersCurrent(): Promise<number> {
    return this.request<number>(`/v1/admin/users/current`);
  }

  getLoginHeatmap(params?: {
    resolution?: 3 | 4 | 5 | 6 | 7;
    since?: Date;
    minCount?: number;
  }): Promise<LoginHeatmapCell[]> {
    const q = new URLSearchParams();
    if (params?.resolution) q.set("resolution", String(params.resolution));
    if (params?.since) q.set("since", params.since.toISOString());
    if (params?.minCount) q.set("min_count", String(params.minCount));
    const qs = q.toString();
    return this.request<LoginHeatmapCell[]>(
      `/v1/admin/users/login-heatmap${qs ? `?${qs}` : ""}`,
    );
  }

  // Always 288 buckets (one per 5 minutes of the day, UTC) — never sparse.
  getUserLoginTimeHeatmap(
    userId: string,
    days?: number,
  ): Promise<LoginTimeBucket[]> {
    const query = days !== undefined ? `?days=${days}` : "";
    return this.request<LoginTimeBucket[]>(
      `/v1/admin/users/${encodeURIComponent(userId)}/login-heatmap-5m${query}`,
    );
  }

  // Same shape as getUserLoginTimeHeatmap, aggregated across every user.
  getLoginTimeHeatmap(
    days?: number,
    ignoreSpecialUsers: boolean = true,
  ): Promise<LoginTimeBucket[]> {
    const q = new URLSearchParams();
    if (days !== undefined) q.set("days", String(days));
    q.set("ignore_special_users", String(ignoreSpecialUsers));
    return this.request<LoginTimeBucket[]>(
      `/v1/admin/users/login-heatmap-5m?${q}`,
    );
  }

  // Sparse: days with zero logins are omitted. Sorted by date ascending.
  getLoginsPerDay(
    days?: number,
    ignoreSpecialUsers: boolean = true,
  ): Promise<LoginDailyCount[]> {
    const q = new URLSearchParams();
    if (days !== undefined) q.set("days", String(days));
    q.set("ignore_special_users", String(ignoreSpecialUsers));
    return this.request<LoginDailyCount[]>(
      `/v1/admin/users/logins-per-day?${q}`,
    );
  }

  getUserLoginHistory(
    userId: string,
    page: number = 1,
  ): Promise<LoginHistoryPage> {
    return this.request<LoginHistoryPage>(
      `/v1/admin/users/${encodeURIComponent(userId)}/login-history?page=${page}`,
    );
  }

  // No `days` filter exists on this endpoint, so this walks every page —
  // capped so a heavy user's history can't turn into an unbounded fetch loop.
  private static readonly LOGIN_HISTORY_MAX_PAGES = 20;

  async getAllUserLoginHistory(userId: string): Promise<LoginEvent[]> {
    const events: LoginEvent[] = [];
    let page = 1;
    while (page <= AdminProvider.LOGIN_HISTORY_MAX_PAGES) {
      const res = await this.getUserLoginHistory(userId, page);
      events.push(...res.data);
      if (!res.has_next) break;
      page++;
    }
    return events;
  }
}

export const adminProvider = new AdminProvider();
